import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { buildPlayerPagePath } from "../../pages/Player/playerTabs";
import "./draftOddsBoard.css";

export const DEFAULT_PLAYER_IMAGE =
    "https://awoiaf.westeros.org/images/thumb/5/56/No_portrait.svg/240px-No_portrait.svg.png";

export function formatProspectName(player) {
    return `${player.first_name || ""} ${player.last_name || ""}`.trim();
}

export function computeProspectStats(prospect) {
    const distribution = prospect.distribution || [];
    const pTop32 = distribution
        .filter((d) => d.pick <= 32)
        .reduce((sum, d) => sum + d.odds, 0);
    const pTop10 = distribution
        .filter((d) => d.pick <= 10)
        .reduce((sum, d) => sum + d.odds, 0);

    return {
        pTop32: prospect.p_top32 ?? pTop32,
        pTop10: prospect.p_top10 ?? pTop10,
    };
}

export function DistributionStrip({ distribution, depth = 32, compact = false }) {
    const cells = useMemo(() => {
        const byPick = new Map((distribution || []).map((d) => [d.pick, d.odds]));
        const maxOdds = Math.max(...(distribution || []).map((d) => d.odds), 0.001);
        return Array.from({ length: depth }, (_, index) => {
            const pick = index + 1;
            const odds = byPick.get(pick) || 0;
            return {
                pick,
                odds,
                intensity: odds / maxOdds,
            };
        });
    }, [distribution, depth]);

    return (
        <div
            className={`draft-odds-strip${compact ? " draft-odds-strip--compact" : ""}`}
            style={{ gridTemplateColumns: `repeat(${depth}, minmax(0, 1fr))` }}
            aria-hidden="true"
        >
            {cells.map((cell) => (
                <div
                    key={cell.pick}
                    className="draft-odds-strip-cell"
                    style={{ "--intensity": cell.intensity }}
                    title={`Pick ${cell.pick}: ${(cell.odds * 100).toFixed(1)}%`}
                />
            ))}
        </div>
    );
}

export function PickSlotCard({ slot }) {
    const leader = slot.candidates[0];

    return (
        <article className="draft-odds-pick-card">
            <div className="draft-odds-pick-header">
                <div className="draft-odds-pick-badge">
                    <span className="draft-odds-pick-num">#{slot.pick}</span>
                    <span className="draft-odds-pick-round">R{slot.round}</span>
                </div>
                {slot.team_logo && (
                    <img
                        src={slot.team_logo}
                        alt=""
                        className="draft-odds-team-logo"
                        loading="lazy"
                    />
                )}
                <span className="draft-odds-team-name">{slot.team_name}</span>
            </div>

            {slot.candidates.length === 0 ? (
                <p className="draft-odds-empty-pick">No odds yet</p>
            ) : (
                <div className="draft-odds-candidates">
                    {slot.candidates.map((candidate, index) => (
                        <Link
                            key={candidate.player_id}
                            to={buildPlayerPagePath(candidate.player_id, "draft-odds")}
                            className={`draft-odds-candidate${index === 0 ? " draft-odds-candidate--leader" : ""}`}
                        >
                            <div className="draft-odds-candidate-top">
                                <span className="draft-odds-candidate-name">
                                    {formatProspectName(candidate)}
                                </span>
                                <span className="draft-odds-candidate-pct">
                                    {(candidate.odds * 100).toFixed(0)}%
                                </span>
                            </div>
                            <div className="draft-odds-candidate-bar">
                                <div
                                    className="draft-odds-candidate-bar-fill"
                                    style={{ width: `${Math.min(candidate.odds * 100, 100)}%` }}
                                />
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {leader && (
                <div className="draft-odds-pick-footnote">
                    Most likely: {formatProspectName(leader)}
                </div>
            )}
        </article>
    );
}

export function ProspectCard({ prospect, distributionDepth = 32 }) {
    const stats = computeProspectStats(prospect);

    return (
        <Link
            to={buildPlayerPagePath(prospect.player_id, "draft-odds")}
            className="draft-odds-prospect-card"
        >
            <div className="draft-odds-prospect-head">
                <span className="draft-odds-prospect-rank">#{prospect.consensus_ranking}</span>
                <div className="draft-odds-prospect-identity">
                    <img
                        src={prospect.player_image_url || DEFAULT_PLAYER_IMAGE}
                        alt=""
                        className="draft-odds-prospect-avatar"
                        loading="lazy"
                    />
                    <div className="draft-odds-prospect-text">
                        <span className="draft-odds-prospect-name">{formatProspectName(prospect)}</span>
                        <span className="draft-odds-prospect-meta">
                            {prospect.position}
                            {prospect.flag_url && (
                                <img src={prospect.flag_url} alt="" className="draft-odds-flag" loading="lazy" />
                            )}
                        </span>
                    </div>
                </div>
                <div className="draft-odds-prospect-exp">
                    <span className="draft-odds-prospect-exp-label">Exp.</span>
                    <span className="draft-odds-prospect-exp-value">{prospect.expected_pick.toFixed(1)}</span>
                </div>
            </div>

            <DistributionStrip distribution={prospect.distribution} depth={distributionDepth} />

            <div className="draft-odds-prospect-stats">
                <span>{(prospect.p_first * 100).toFixed(0)}% at #1</span>
                <span>{(prospect.p_top5 * 100).toFixed(0)}% top 5</span>
                <span>Most likely #{prospect.modal_pick}</span>
            </div>
        </Link>
    );
}
